"use client"

import { CircleHelp } from "lucide-react"
import React from "react"

import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

export const METRIC_TOOLTIP_TITLE_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-medium text-[color:var(--brand-tropical-surf)]"

export const METRIC_TOOLTIP_BODY_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-medium text-gray-500 dark:text-white"

const METRIC_TOOLTIP_FONT_CLASS = "font-[family-name:var(--font-primary)] text-xs"

/** Matches ChartTooltipContent shell — even ambient shadow (all edges), not a directional drop. */
export const METRIC_TOOLTIP_SHELL_CLASS =
  "rounded-lg border border-border/50 bg-background text-foreground shadow-[0_0_12px_rgba(15,23,42,0.12),0_0_2px_rgba(15,23,42,0.08)] dark:shadow-[0_0_14px_rgba(0,0,0,0.55),0_0_2px_rgba(0,0,0,0.35)]"

/** Radix arrow uses fill on its SVG polygon — set directly, not via parent selectors. */
export const METRIC_TOOLTIP_ARROW_CLASS_NAME =
  "bg-background fill-[var(--background)]"

export function metricTooltipShellClassName(widthClass = "max-w-xs"): string {
  return cn(
    METRIC_TOOLTIP_SHELL_CLASS,
    METRIC_TOOLTIP_FONT_CLASS,
    // Keep flex (not `block`) so Radix arrow positioning stays intact inside TooltipContent.
    "flex flex-col items-start px-2.5 py-1.5 text-left",
    widthClass
  )
}

function MetricTooltipContent({
  className,
  widthClass = "max-w-xs",
  ...props
}: React.ComponentProps<typeof TooltipContent> & { widthClass?: string }) {
  return (
    <TooltipContent
      arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
      className={cn(metricTooltipShellClassName(widthClass), className)}
      {...props}
    />
  )
}

const METRIC_DEFINITION_TRIGGER_CLASS =
  "cursor-help font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500 underline decoration-dotted underline-offset-2 dark:text-white"

function MetricTooltipBody({
  label,
  description,
}: {
  label?: string
  description: string
}) {
  return (
    <>
      {label ? <p className={METRIC_TOOLTIP_TITLE_CLASS}>{label}</p> : null}
      <p
        className={cn(
          "text-[11px] leading-snug",
          label && "mt-1",
          METRIC_TOOLTIP_BODY_CLASS
        )}
      >
        {description}
      </p>
    </>
  )
}

/** Dotted-underline metric name in tables (risk metrics, system quality, etc.). */
export function MetricDefinitionTooltip({
  label,
  description,
}: {
  label: string
  description: string
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={METRIC_DEFINITION_TRIGGER_CLASS}>{label}</span>
        </TooltipTrigger>
        <MetricTooltipContent side="top">
          <MetricTooltipBody label={label} description={description} />
        </MetricTooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** Circle-help icon on mini stat cards and lifted title cards. */
export function MetricHelpIconTooltip({
  description,
  label,
  side = "top",
  className,
  iconClassName,
}: {
  description: string
  label?: string
  side?: "top" | "bottom" | "left" | "right"
  className?: string
  iconClassName?: string
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="shrink-0 cursor-help">
            <CircleHelp
              className={cn(
                "size-3 text-muted-foreground dark:text-sidebar-muted-foreground",
                iconClassName
              )}
              aria-hidden
            />
          </button>
        </TooltipTrigger>
        <MetricTooltipContent side={side} widthClass="max-w-[300px]" className={className}>
          <MetricTooltipBody label={label} description={description} />
        </MetricTooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
